import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useFetch } from "@/lib/useFetch";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { PageHeader } from "@/mockups/ui";
import { HoursExcelImport } from "@/components/HoursExcelImport";
import { FileInput } from "@/components/FileInput";
import { toast } from "sonner";
import { Link2, RefreshCw, Plug, CheckCircle2, Building2, Contact, FileText, Unplug, Loader2, Briefcase, Upload, Receipt, AlertTriangle, Target, Clock, Wallet } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("es-ES", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

const PHASE_LABEL = {
  companies: "Importando empresas…",
  contacts: "Importando contactos…",
  deals: "Importando presupuestos…",
  projects: "Importando proyectos…",
  linking: "Vinculando contactos con sus empresas…",
  invoices: "Importando facturas…",
  milestones: "Importando hitos…",
  hours: "Importando horas registradas…",
  economics: "Importando costes externos…",
  done: "Sincronización completada",
};

function StatBox({ icon: Icon, label, created, updated }) {
  return (
    <div className="rounded-lg border border-border p-4 min-w-0">
      <div className="flex items-center gap-2 text-muted-foreground text-sm mb-3">
        <Icon className="h-4 w-4 shrink-0" /> <span className="truncate">{label}</span>
      </div>
      <div className="flex items-end gap-5">
        <div>
          <div className="text-2xl font-heading font-bold text-primary tabular-nums leading-none">{created ?? 0}</div>
          <div className="text-xs text-muted-foreground mt-1.5">nuevos</div>
        </div>
        <div>
          <div className="text-2xl font-heading font-bold text-gold-foreground dark:text-gold tabular-nums leading-none">{updated ?? 0}</div>
          <div className="text-xs text-muted-foreground mt-1.5">actualizados</div>
        </div>
      </div>
    </div>
  );
}

function CountBox({ icon: Icon, label, value, sub = "importados" }) {
  return (
    <div className="rounded-lg border border-border p-4 min-w-0">
      <div className="flex items-center gap-2 text-muted-foreground text-sm mb-3">
        <Icon className="h-4 w-4 shrink-0" /> <span className="truncate">{label}</span>
      </div>
      <div className="text-2xl font-heading font-bold text-primary tabular-nums leading-none">{value ?? 0}</div>
      <div className="text-xs text-muted-foreground mt-1.5">{sub}</div>
    </div>
  );
}

export default function Ajustes() {
  const { can, user } = useAuth();
  const canEdit = can("settings", "edit");
  const canImport = ["Administrador", "Directora"].includes(user?.role);
  const { data: status, loading, refetch } = useFetch("/teamleader/status");
  const [connecting, setConnecting] = useState(false);
  const [params, setParams] = useSearchParams();
  const startedToast = useRef(false);

  useEffect(() => {
    const tl = params.get("tl");
    if (tl === "connected") { toast.success("Teamleader conectado · iniciando importación…"); startSync(true, "full"); }
    else if (tl === "error") { toast.error("No se pudo conectar con Teamleader. Inténtalo de nuevo."); }
    if (tl) { params.delete("tl"); setParams(params, { replace: true }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll while a sync is running
  const syncing = status?.syncing;
  useEffect(() => {
    if (!syncing) return;
    const id = setInterval(() => refetch(), 3000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncing]);

  // Toast when a running sync finishes
  const prevSyncing = useRef(false);
  useEffect(() => {
    if (prevSyncing.current && !syncing && status?.last_sync) {
      const r = status.last_sync;
      if (r.scope === "crm") {
        toast.success(`Importación completada · ${(r.companies?.created || 0) + (r.companies?.updated || 0)} empresas, ${(r.contacts?.created || 0) + (r.contacts?.updated || 0)} contactos (${r.linked || 0} vinculados)`);
      } else {
        toast.success(`Sincronización completada · ${(r.projects?.created || 0) + (r.projects?.updated || 0)} proyectos, ${r.milestones || 0} hitos, ${r.time_entries || 0} registros de horas, ${r.invoices || 0} facturas`);
      }
    }
    prevSyncing.current = syncing;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncing]);

  const connect = async () => {
    setConnecting(true);
    try {
      const { data } = await api.get("/teamleader/oauth/authorize-url");
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo iniciar la conexión");
      setConnecting(false);
    }
  };

  const startSync = async (silent = false, scope = "operational") => {
    try {
      const { data } = await api.post("/teamleader/sync", null, { params: { scope } });
      if (data.already_running && !silent) toast.info("Ya hay una sincronización en curso");
      else if (!silent) toast.info(scope === "crm" ? "Importando empresas y contactos…" : "Sincronizando proyectos, facturas y horas…");
      await refetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al iniciar la sincronización");
    }
  };

  const disconnect = async () => {
    if (!window.confirm("¿Desconectar Teamleader? Los datos ya importados se conservan.")) return;
    try { await api.post("/teamleader/disconnect"); toast.success("Teamleader desconectado"); refetch(); }
    catch (e) { toast.error("Error al desconectar"); }
  };

  const connected = status?.connected;
  const last = status?.last_sync;
  const prog = status?.progress;

  // --- Importador de costes externos (Excel) ---
  const [costFile, setCostFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [costResult, setCostResult] = useState(null);

  const importCosts = async () => {
    if (!costFile) return;
    setImporting(true);
    setCostResult(null);
    try {
      const fd = new FormData();
      fd.append("file", costFile);
      const { data } = await api.post("/teamleader/import-external-costs", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCostResult(data);
      toast.success(`Importados ${data.imported} costes externos en ${data.projects_matched} proyectos`);
      setCostFile(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo importar el archivo");
    } finally {
      setImporting(false);
    }
  };

  // Progress bar %: during linking use processed/total; otherwise indeterminate-ish by phase
  let pct = 0;
  if (prog) {
    const phaseIdx = { companies: 10, contacts: 30, deals: 45, projects: 55, linking: 62, invoices: 72, milestones: 80, hours: 90, economics: 96, done: 100 }[prog.phase] || 0;
    if (prog.phase === "linking" && prog.contacts_total > 0) {
      pct = 60 + Math.round((prog.processed / prog.contacts_total) * 2);
    } else { pct = phaseIdx; }
  }

  return (
    <div className="space-y-6 animate-fade-in-up">
      <PageHeader title="Integraciones" subtitle="Conecta herramientas externas para centralizar la información de la agencia" />

      <Card className="border-border shadow-none p-6 max-w-3xl" data-testid="teamleader-card">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-lg bg-navy text-white flex items-center justify-center shrink-0">
            <Plug className="h-6 w-6" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-heading font-bold text-lg">Teamleader Focus</h3>
              {connected
                ? <Badge className="bg-primary/12 text-primary border-transparent gap-1"><CheckCircle2 className="h-3 w-3" /> Conectado</Badge>
                : <Badge variant="secondary" className="font-normal">No conectado</Badge>}
            </div>
            <p className="text-sm text-muted-foreground mt-1 max-w-md">
              Importa tus empresas, contactos y presupuestos desde Teamleader. La sincronización es unidireccional: solo lee datos de Teamleader.
            </p>
            <p className="text-xs text-muted-foreground mt-2 max-w-md">
              <b>Sincronizar proyectos</b> actualiza lo que cambia a menudo (proyectos, facturas, hitos y horas) y es rápido. <b>Empresas y contactos</b> es más lento y solo hace falta de vez en cuando, ya que apenas cambian.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="py-10 flex justify-center"><div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>
        ) : !status?.configured ? (
          <div className="mt-6 rounded-lg bg-destructive/8 text-destructive text-sm p-4">
            La integración no está configurada (faltan credenciales de la App de Teamleader).
          </div>
        ) : (
          <>
            {syncing && (
              <div className="mt-6 rounded-lg border border-primary/20 bg-primary/[0.04] p-4 space-y-3" data-testid="teamleader-progress">
                <div className="flex items-center gap-2 text-sm font-medium text-primary">
                  <Loader2 className="h-4 w-4 animate-spin" /> {PHASE_LABEL[prog?.phase] || "Sincronizando…"}
                </div>
                <Progress value={pct} className="h-2" />
                <div className="text-xs text-muted-foreground">
                  {prog?.phase === "linking"
                    ? `Vinculando ${prog?.processed || 0} de ${prog?.contacts_total || 0} contactos · ${prog?.linked || 0} con empresa`
                    : `Empresas: ${(prog?.companies?.created || 0) + (prog?.companies?.updated || 0)} · Contactos: ${(prog?.contacts?.created || 0) + (prog?.contacts?.updated || 0)} · Presupuestos: ${(prog?.deals?.created || 0) + (prog?.deals?.updated || 0)}`}
                </div>
                <p className="text-xs text-muted-foreground">Puedes seguir usando la app; la importación continúa en segundo plano.</p>
              </div>
            )}

            {connected && status?.auto_sync?.enabled && (
              <div className="mt-6 flex items-start gap-2 rounded-lg border border-primary/20 bg-primary/[0.04] p-3 text-xs text-muted-foreground" data-testid="teamleader-autosync-note">
                <RefreshCw className="h-3.5 w-3.5 mt-0.5 text-primary shrink-0" />
                <span>
                  <b className="text-foreground">Sincronización automática activada.</b> Cada noche (~{String(status.auto_sync.hour).padStart(2, "0")}:00) se actualizan proyectos, facturas, hitos y horas, y se refresca el cashflow. No hace falta pulsar nada.
                  {status.auto_sync.last_date && <span className="ml-1">Último auto-sync: {status.auto_sync.last_date}.</span>}
                </span>
              </div>
            )}

            {status?.error && !syncing && (
              <div className="mt-6 rounded-lg bg-destructive/8 text-destructive text-sm p-4">Último error: {status.error}</div>
            )}

            {connected && !syncing && last && (
              <div className="mt-6 space-y-4">
                <div className="text-sm text-muted-foreground">
                  Última sincronización: <span className="text-foreground font-medium">{fmtDate(status?.last_sync_at)}</span>
                  {typeof last.linked === "number" && <span className="ml-2">· {last.linked} contactos vinculados a su empresa</span>}
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  <StatBox icon={Building2} label="Empresas" created={last.companies?.created} updated={last.companies?.updated} />
                  <StatBox icon={Contact} label="Contactos" created={last.contacts?.created} updated={last.contacts?.updated} />
                  <StatBox icon={FileText} label="Presupuestos" created={last.deals?.created} updated={last.deals?.updated} />
                  <StatBox icon={Briefcase} label="Proyectos" created={last.projects?.created} updated={last.projects?.updated} />
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  <CountBox icon={Target} label="Hitos" value={last.milestones} />
                  <CountBox icon={Clock} label="Horas" value={last.time_entries} sub="registros" />
                  <CountBox icon={Receipt} label="Facturas" value={last.invoices} />
                  <CountBox icon={Wallet} label="Costes externos" value={last.economics} />
                </div>
              </div>
            )}

            <div className="mt-6 flex flex-wrap items-center gap-3">
              {!connected && canEdit && (
                <Button onClick={connect} disabled={connecting} className="gap-2" data-testid="teamleader-connect-btn">
                  <Link2 className="h-4 w-4" /> {connecting ? "Redirigiendo…" : "Conectar Teamleader"}
                </Button>
              )}
              {connected && canEdit && (
                <>
                  <Button onClick={() => startSync(false, "operational")} disabled={syncing} className="gap-2" data-testid="teamleader-sync-btn">
                    <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Sincronizando…" : "Sincronizar proyectos"}
                  </Button>
                  <Button onClick={() => startSync(false, "crm")} disabled={syncing} variant="outline" className="gap-2" data-testid="teamleader-sync-crm-btn">
                    <Building2 className="h-4 w-4" /> Empresas y contactos
                  </Button>
                  <Button onClick={disconnect} variant="outline" disabled={syncing} className="gap-2 text-destructive hover:text-destructive" data-testid="teamleader-disconnect-btn">
                    <Unplug className="h-4 w-4" /> Desconectar
                  </Button>
                </>
              )}
              {!canEdit && (
                <p className="text-sm text-muted-foreground">Solo un Administrador puede conectar o sincronizar.</p>
              )}
            </div>
          </>
        )}
      </Card>

      {canImport && (
        <Card className="border-border shadow-none p-6 max-w-3xl" data-testid="external-costs-card">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-lg bg-primary text-primary-foreground flex items-center justify-center shrink-0">
              <Receipt className="h-6 w-6" />
            </div>
            <div>
              <h3 className="font-heading font-bold text-lg">Costes externos (Excel)</h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-md">
                Los costes externos de proveedores no los expone la API de Teamleader. Exporta el listado
                a Excel (con las columnas <b>Costes</b> y <b>Nº de proyecto</b>) y súbelo aquí: repartimos
                cada coste a su proyecto (por referencia) como "Gasto previsto". Puedes volver a subirlo
                cuando quieras; sustituye la importación anterior.
              </p>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <FileInput file={costFile} onFile={setCostFile} accept=".xlsx,.xls" testid="external-costs-file-input" />
            <Button onClick={importCosts} disabled={!costFile || importing} className="gap-2" data-testid="external-costs-import-btn">
              {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {importing ? "Importando…" : "Importar costes"}
            </Button>
          </div>

          {costResult && (
            <div className="mt-6 rounded-lg border border-primary/20 bg-primary/[0.04] p-4 space-y-2" data-testid="external-costs-result">
              <div className="flex items-center gap-2 text-sm font-medium text-primary">
                <CheckCircle2 className="h-4 w-4" /> Importación completada
              </div>
              <div className="text-sm text-foreground">
                <b>{costResult.imported}</b> costes importados en <b>{costResult.projects_matched}</b> proyectos ·
                total <b>{costResult.total_amount?.toLocaleString("es-ES", { style: "currency", currency: "EUR" })}</b>
              </div>
              {costResult.unmatched_count > 0 && (
                <div className="flex items-start gap-2 text-xs text-gold-foreground dark:text-gold">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  <span>
                    {costResult.unmatched_count} referencias del Excel no coinciden con ningún proyecto
                    {costResult.unmatched_refs?.length ? ` (ej.: ${costResult.unmatched_refs.slice(0, 10).join(", ")})` : ""}.
                  </span>
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      {canImport && <HoursExcelImport />}
    </div>
  );
}
